import React, { Fragment, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment from 'moment';
import PropTypes from 'prop-types';

import { requestList as request } from 'api/sfms/exchangeRate';
import { requestList as requestCode } from 'api/standard/code';
import Select from 'components/common-sfims/form/Select';
import CommonList from 'components/common-sfims/List';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText, utc2tz } from 'function/convert';
import { onInputChange } from 'function/event';
import { getText } from 'function/language';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import formatNumber from 'utils/format/number';
import format from 'utils/string/format';

/**
 * 배합비 작성 정보 관리 > 원료 가격 - 환율 히스토리 목록
 */
const List = ({ show, onHide }) => {
  /* state */
  // 국가 목록
  const [countries, setCountries] = useState([]);
  // 목록
  const [datas, setDatas] = useState([]);
  // 목록 끝 번호
  const [end, setEnd] = useState(15);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 환율 히스토리 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({});
  // 총 데이터 수
  const [total, setTotal] = useState(0);

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우 국가 목록 조회 요청
    if (show) {
      requestCountries();
    }
  }, [show]);
  useEffect(() => {
    // 모달이 표시된 경우 환율 히스토리 목록 조회 요청
    if (show) {
      requestList();
    }
  }, [show, params]);

  /* method */
  // 환율 히스토리 목록 조회 요청
  const requestList = async () => {
    setDatas([]);
    setEnd(15);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(list);
      setTotal(totalCount);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 국가 목록 조회 요청
  const requestCountries = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'country' });

    setCountries(list);
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      size="lg"
      onHide={onHide}
    >
      <Fragment key="header">{getText('exchangeRateList')}</Fragment>
      <Fragment key="body">
        <Select
          datas={array2ValueAndText(countries, 'codeCd', 'codeNm')}
          value={params.nationCode}
          showAll
          className="w-25 mb-3"
          name="nationCode"
          onChange={event => onInputChange(event, setParams)}
        />
        <div id="scroll" className="max-vh-50 scrollbar">
          <InfiniteScroll
            hasMore={end < total}
            dataLength={datas.slice(0, end).length}
            scrollableTarget="scroll"
            next={() => setEnd(end + 15)}
          >
            <CommonList
              columns={[
                'month',
                'nation',
                'nationCurr',
                'rate',
                'regDt',
                'regUsrNm'
              ]}
              message={message}
              showLoading={isLoading}
            >
              {datas
                .slice(0, end)
                .map(
                  (
                    {
                      exchangeYm,
                      nationCode,
                      nationNm,
                      currency,
                      rate,
                      regDt,
                      regUsrNm
                    } = {},
                    index
                  ) => (
                    <tr key={`datas${index}`}>
                      <td>
                        {moment(exchangeYm).format(
                          format(getText('ym'), {
                            year: 'YY',
                            month: 'M'
                          })
                        )}
                      </td>
                      <td>{nationNm}</td>
                      <td>{currency}</td>
                      <td className="text-end">
                        {formatNumber(rate, ',0.00')}
                      </td>
                      <td>{utc2tz(regDt)}</td>
                      <td>{regUsrNm}</td>
                    </tr>
                  )
                )}
            </CommonList>
          </InfiniteScroll>
        </div>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('close')}
        </Button>
      </Fragment>
    </Modal>
  );
};

List.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func
};
List.defaultProps = { show: false };

export default List;
